import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';

export default function CreateIncidentModal({ open, onClose, onCreated }) {
  const { getToken } = useAuth();
  const { activeCompany } = useTenant();

  const [form, setForm] = useState({
    incident_type: 'Phishing',
    title: '',
    date: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
    severity: 'Medium',
    status: 'Open',
    tlp: 'Amber',
    mitre_ttps: [],
    assigned: '',
    asset: '',
    details: '',
    recommendations: '',
    case_notes: ''
  });

  const [mitreTtpInput, setMitreTtpInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // State for company users
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add function to handle adding TTPs
  const addMitreTtp = () => {
    const ttp = mitreTtpInput.trim().toUpperCase();
    if (ttp && /^T\d{4}(\.\d{3})?$/.test(ttp)) {
      if (!form.mitre_ttps.includes(ttp)) {
        setForm(f => ({ ...f, mitre_ttps: [...f.mitre_ttps, ttp] }));
      }
      setMitreTtpInput('');
    }
  };

  const removeMitreTtp = (ttp) => {
    setForm(f => ({ ...f, mitre_ttps: f.mitre_ttps.filter(t => t !== ttp) }));
  };

  // Fetch company users when modal opens
  useEffect(() => {
    if (!open || !activeCompany) return;
    
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = await getToken();
        const res = await fetch(`/api/users?companyId=${activeCompany.numeric_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const users = await res.json();
          // Filter to only active users
          setCompanyUsers(users.filter(u => u.active));
        }
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    fetchUsers();
  }, [open, activeCompany, getToken]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!activeCompany) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      const res = await fetch(`/api/companies/${activeCompany.id}/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Create failed (${res.status}) ${t}`);
      }
      const data = await res.json();
      onCreated?.(data);
      onClose?.();
      
      // Reset form
      setForm({
        incident_type: 'Phishing',
        title: '',
        date: new Date().toISOString().slice(0, 10),
        severity: 'Medium',
        status: 'Open',
        tlp: 'Amber',
        mitre_ttps: [],
        assigned: '',
        asset: '',
        details: '',
        recommendations: '',
        case_notes: ''
      });
      setMitreTtpInput('');
    } catch (err) {
      setError(err.message || 'Failed to create incident');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-100">New Incident</h3>
          <button
            className="text-slate-400 hover:text-slate-200"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded p-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Type *</label>
              <select
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.incident_type}
                onChange={update('incident_type')}
                required
              >
                <option>Phishing</option>
                <option>Malware</option>
                <option>Credential Theft</option>
                <option>Dark Web</option>
                <option>Brand Abuse</option>
                <option>Domain Impersonation</option>
                <option>Impersonating Social Media Account</option>
                <option>Suspicious Social Media Content</option>
                <option>Employee Credentials Found</option>
                <option>Suspicious Dark Web Content</option>
                <option>Suspicious Code Repo Detected</option>
                <option>Sensitive Repo Content</option>
                <option>VIP Credential</option>
                <option>VIP Information</option>
                <option>Impersonating Domain Change</option>
                <option>Cyber Threat Intelligence Update</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">Date *</label>
              <input
                type="date"
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.date}
                onChange={update('date')}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Title *</label>
            <input
              type="text"
              className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
              placeholder="Concise incident title"
              value={form.title}
              onChange={update('title')}
              required
            />
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Severity</label>
              <select
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.severity}
                onChange={update('severity')}
              >
                <option>Critical</option>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
                <option>Informational</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.status}
                onChange={update('status')}
              >
                <option>Open</option>
                <option>Monitoring</option>
                <option>Takedown Requested</option>
                <option>Resolved</option>
                <option>Resolved - Nonissue</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-300 mb-1">TLP</label>
              <select
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.tlp}
                onChange={update('tlp')}
              >
                <option>Red</option>
                <option>Amber</option>
                <option>Green</option>
                <option>Clear</option>
              </select>
            </div>

            {/* MITRE ATT&CK TTPs */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                MITRE TTPs
                <span className="text-slate-500 ml-1 text-xs">(T1566)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 text-sm"
                  placeholder="T1566"
                  value={mitreTtpInput}
                  onChange={(e) => setMitreTtpInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addMitreTtp();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addMitreTtp}
                  className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-xs text-cyan-400"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* MITRE TTP Tags Display */}
          {form.mitre_ttps.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {form.mitre_ttps.map((ttp) => (
                <span
                  key={ttp}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-300"
                >
                  {ttp}
                  <button
                    type="button"
                    onClick={() => removeMitreTtp(ttp)}
                    className="hover:text-purple-100"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Assigned To</label>
              <select
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.assigned}
                onChange={update('assigned')}
                disabled={loadingUsers}
              >
                <option value="">Unassigned</option>
                {loadingUsers ? (
                  <option disabled>Loading users...</option>
                ) : (
                  companyUsers.map((user) => (
                    <option key={user.id} value={user.email}>
                      {user.first_name && user.last_name 
                        ? `${user.first_name} ${user.last_name} (${user.email})`
                        : user.email
                      }
                    </option>
                  ))
                )}
              </select>
              {loadingUsers && (
                <p className="text-xs text-slate-500 mt-1">Loading analysts...</p>
              )}
              {!loadingUsers && companyUsers.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">No users available</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm text-slate-300 mb-1">Asset</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.asset}
                onChange={update('asset')}
                placeholder="domain.com / handle / system name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Details</label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.details}
                onChange={update('details')}
                placeholder="What happened?"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Recommendations</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.recommendations}
                onChange={update('recommendations')}
                placeholder="Next steps / actions"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Case Notes</label>
              <textarea
                rows={2}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200"
                value={form.case_notes}
                onChange={update('case_notes')}
                placeholder="Internal notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-100"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-sm text-white disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Create Incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}